import { clsx } from "clsx";
import { X } from "lucide-react";
import { type ReactNode, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useFocusTrap } from "../../hooks/useFocusTrap";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  className,
}: ModalProps) {
  const trapRef = useFocusTrap(isOpen);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      ref={trapRef}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-black/50"
        onClick={onClose}
        tabIndex={-1}
        aria-label="Zamknij"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx(
          // Never taller than the viewport: header stays, content scrolls.
          "relative z-10 mx-4 flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col rounded-xl bg-panel shadow-xl animate-fade-in",
          className,
        )}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-edge px-6 py-4">
          <h2 className="text-lg font-semibold text-main">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-faint transition-colors hover:bg-muted hover:text-sub"
            aria-label="Zamknij"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
